import React from "react";
import { useTranslation } from "react-i18next";
import { MetricCard } from "@/components/common/MetricCard";
import { PlatformKpiDelta, PlatformKpiTotals } from "@/types/admin-analytics";
import {
  PLATFORM_KPI_CARD_DELTA_KEYS,
  PLATFORM_KPI_CARD_TITLE_KEYS,
  PlatformKpiCardId,
} from "../constants";
import { formatCents, formatCount, formatDeltaBadge } from "../utils/format";

interface PlatformKpiGridProps {
  totals: PlatformKpiTotals;
  compare: boolean;
  cardOrder: PlatformKpiCardId[];
  visibleCards: Set<PlatformKpiCardId>;
  delta?: PlatformKpiDelta;
}

const formatFunnel = (
  completed: number,
  started: number,
  conversionRate: number,
): string =>
  `${formatCount(completed)} / ${formatCount(started)} (${conversionRate}%)`;

const CARD_VALUE_GETTERS: Record<
  PlatformKpiCardId,
  (totals: PlatformKpiTotals) => string
> = {
  preCheckIn: (totals) =>
    formatFunnel(
      totals.preCheckInCompleted,
      totals.preCheckInStarted,
      totals.preCheckInConversionRate,
    ),
  preCheckOut: (totals) =>
    formatFunnel(
      totals.preCheckOutCompleted,
      totals.preCheckOutStarted,
      totals.preCheckOutConversionRate,
    ),
  offers: (totals) =>
    formatFunnel(
      totals.offersPurchased,
      totals.offersOffered,
      totals.offerConversionRate,
    ),
  offerRevenue: (totals) => formatCents(totals.offerRevenueCents),
  moduleUsage: (totals) => formatCount(totals.moduleUsageTotal),
  uniqueActiveGuests: (totals) => formatCount(totals.uniqueActiveGuests),
};

export const PlatformKpiGrid: React.FC<PlatformKpiGridProps> = ({
  totals,
  compare,
  cardOrder,
  visibleCards,
  delta,
}) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
      {cardOrder
        .filter((id) => visibleCards.has(id))
        .map((id) => {
          const deltaBadge = compare
            ? formatDeltaBadge(delta?.[PLATFORM_KPI_CARD_DELTA_KEYS[id]], t)
            : null;

          return (
            <MetricCard
              key={id}
              width="fill"
              title={t(PLATFORM_KPI_CARD_TITLE_KEYS[id])}
              value={CARD_VALUE_GETTERS[id](totals)}
              delta={deltaBadge?.delta}
              trend={deltaBadge?.trend}
            />
          );
        })}
    </div>
  );
};
